import { useMutation, useQueryClient } from "@tanstack/react-query";

import { onToast } from "@components/Toast";

import { processRequest } from "@api/config";
import { IAddCustomStreamLinkRequest, IEditCustomStreamLinkRequest, customStreamAPI } from "@api/customStream";

import { useAuthQuery } from "@lib/queryClient";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";

export const useCustomStreamLinksQuery = () =>
	useAuthQuery({
		queryKey: [QUERY_KEYS.CUSTOM_STREAM_LINKS],
		queryFn: async () => {
			const data = await processRequest({
				prefix: API_PREFIXES.GET_CUSTOM_STREAM_LINKS,
				request: (token) => customStreamAPI.getCustomStreamLinksAsync({ token }),
			});

			return data;
		},
	});

export const useAddCustomStreamLinkMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ title, links }: Omit<IAddCustomStreamLinkRequest, "token">) => {
			await processRequest({
				prefix: API_PREFIXES.ADD_CUSTOM_STREAM_LINK,
				request: (token) => customStreamAPI.addCustomStreamLinkAsync({ token, title, links }),
			});
		},
		onSuccess: () => {
			onToast({
				message: "Success",
				description: "Custom stream link added",
			});
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STREAM_LINKS] });
		},
	});
};

export const useEditCustomStreamLinkMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, title, links }: Omit<IEditCustomStreamLinkRequest, "token">) => {
			await processRequest({
				prefix: API_PREFIXES.EDIT_CUSTOM_STREAM_LINK,
				request: (token) => customStreamAPI.editCustomStreamLinkAsync({ token, id, title, links }),
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STREAM_LINKS] });
		},
	});
};

export const useDeleteCustomStreamLinkMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id }: { id: number }) => {
			await processRequest({
				prefix: API_PREFIXES.DELETE_CUSTOM_STREAM_LINK,
				request: (token) => customStreamAPI.deleteCustomStreamLinkAsync({ token, id }),
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STREAM_LINKS] });
		},
	});
};
